import { GiOnTarget, GiSandsOfTime, GiSiren } from "react-icons/gi";
import { useRecallMarch } from "../api/marches";
import { MarchEntry } from "../types/marches";
import { UnitCatalogEntry, UnitClassIcon } from "../types/army";
import { formatDuration, formatNumber, secondsUntil } from "../lib/format";
import { hapticImpact } from "../lib/telegram";

interface Props {
  marches: MarchEntry[];
  now: Date;
  unitsById: Map<string, UnitCatalogEntry>;
  onFindOpponent: () => void;
  onOpenPve: () => void;
  onOpenHistory: () => void;
}

export function MarchesList({
  marches,
  now,
  unitsById,
  onFindOpponent,
  onOpenPve,
  onOpenHistory,
}: Props) {
  const recall = useRecallMarch();

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between px-3 py-2">
        <div className="font-medium">Активные марши</div>
        <button
          type="button"
          onClick={onOpenHistory}
          className="rounded-lg bg-tg-secondaryBg px-3 py-1.5 text-sm"
        >
          История
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 px-3 pb-2">
        <button
          type="button"
          onClick={() => {
            hapticImpact("medium");
            onFindOpponent();
          }}
          className="rounded-lg bg-tg-button px-3 py-3 text-sm font-medium text-tg-buttonText"
        >
          Найти противника
        </button>
        <button
          type="button"
          onClick={() => {
            hapticImpact("medium");
            onOpenPve();
          }}
          className="rounded-lg bg-tg-secondaryBg px-3 py-3 text-sm font-medium"
        >
          Логова монстров
        </button>
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto px-3 pb-3">
        {marches.length === 0 ? (
          <div className="rounded-xl bg-tg-secondaryBg p-4 text-center text-sm text-tg-hint">
            Маршей нет. Найди противника или сходи в PvE.
          </div>
        ) : (
          marches.map((m) => {
            const remaining = secondsUntil(m.arrivesAt, now);
            const units = m.composition?.units ?? {};
            const totalUnits = Object.values(units).reduce(
              (s, v) => s + (typeof v === "number" ? v : 0),
              0,
            );
            const sample = Object.entries(units)
              .slice(0, 3)
              .map(([id, count]) => {
                const u = unitsById.get(id);
                return `${u ? UnitClassIcon[u.cls] : "❓"}${count}`;
              })
              .join(" ");
            return (
              <div key={m.id} className="rounded-xl bg-tg-secondaryBg p-3">
                <div className="mb-1 flex items-center justify-between">
                  <div className="text-sm font-medium inline-flex items-center gap-1.5">
                    {m.direction === "outgoing" ? <GiOnTarget size={14} /> : <GiSiren size={14} />}
                    {m.campName ?? (m.direction === "outgoing" ? "Атака" : "Идёт ко мне")}
                  </div>
                  <div className="text-xs tabular-nums text-tg-accent inline-flex items-center gap-1">
                    <GiSandsOfTime size={12} /> {formatDuration(remaining)}
                  </div>
                </div>
                <div className="text-xs text-tg-hint">
                  {sample} · всего {formatNumber(totalUnits)}
                </div>
                {m.direction === "outgoing" && (
                  <button
                    type="button"
                    disabled={recall.isPending}
                    onClick={() => {
                      hapticImpact("medium");
                      recall.mutate(m.id);
                    }}
                    className="mt-2 w-full rounded-lg bg-tg-bg px-3 py-1.5 text-sm disabled:opacity-40"
                  >
                    ↩ Отозвать
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
